<template>
  <div class="cb-choose-location">
    <div class="cb-choose-location__container">
      <div class="cb-choose-location__header">
        <h1 class="cb-heading-4" tabindex="-1" ref="title">
          It looks like you have multiple locations
        </h1>
        <p class="cb-body-1">
          Please choose the location you are installing your CellBridge device
        </p>
      </div>
      <form>
        <div class="cb-choose-location__body">
          <label
            :class="selectedTile === index ? 'checked' : ''"
            :key="index"
            class="cb-choose-location__body__tile container"
            v-for="(location, index) in locations"
          >
            <div class="cb-choose-location__body__tile__address">
              <p class="cb-body-1">{{ location.siteAddress1 | titleCase }}</p>
              <p class="cb-overline">
                {{ location.city | titleCase }}, {{ location.state }}
                {{ location.zipCode }}
              </p>
            </div>
            <input
              :value="index"
              name="radio"
              type="radio"
              v-model="selectedTile"
            />
          </label>
        </div>
        <div class="cb-choose-location__footer">
          <cb-button
            :disabled="selectedTile === null"
            @click="handleContinueClick"
            :paddingLeft="true"
            :loading="loading"
            type="submit"
          >
            Continue To Next Step
          </cb-button>
          <cb-assistance></cb-assistance>
        </div>
      </form>
      <button
        id="open-nav"
        aria-label="Open Navigation"
        @click="handleNavButtonClick"
        class="nav-button"
      >
        <img
          alt
          src="../assets/icons/menu-icon-white.svg"
        />
      </button>
      <cb-navigation v-if="isNavigationOpen"></cb-navigation>
    </div>
  </div>
</template>

<script>
/* global gtag */
import { mapState, mapActions, mapMutations } from 'vuex';
import { deleteCookie, setCookie } from '../utility/cookie';
import Assistance from '../components/shared/Assistance.vue';
import Button from '../components/shared/Button.vue';
import Navigation from '../components/NavigationModal.vue';

export default {
  data() {
    return {
      selectedTile: null,
      loading: false,
    };
  },
  beforeMount() {
    deleteCookie('selected_site');
  },
  mounted() {
    this.$refs.title.focus();
    document.title = 'CellBridge - Choose current location';
  },
  methods: {
    handleContinueClick() {
      if (this.selectedTile !== null) {
        this.loading = true;
        setCookie('selected_site', this.selectedTile, 0.5);
        this.updateCurrentLocation(this.selectedTile);
        this.getDeviceStatus();
        gtag('event', 'continueClick', {
          event_label: 'continue to next step',
          event_category: 'chooseLocation',
          value: 1,
        });
      }
    },
    handleTileClick(index) {
      this.selectedTile = index;
    },
    handleNavButtonClick() {
      this.setIsNavigationModalOpen(true);
      gtag('event', 'openNavClick', {
        event_label: 'open navigation',
        event_category: 'chooseLocation',
        value: 1,
      });
    },
    ...mapActions(['getDeviceStatus']),
    ...mapMutations(['updateCurrentLocation', 'setIsNavigationModalOpen']),
  },
  computed: {
    ...mapState({
      locations: (state) => state.userInfo.locations,
      isModalOpen: (state) => state.modal.isOpen,
      isNavigationOpen: (state) => state.navigationModal.isOpen,
    }),
  },
  filters: {
    titleCase(value) {
      if (!value) return '';
      const wordsArr = value.toLowerCase().split(' ');
      wordsArr.forEach((word, i) => {
        wordsArr[i] = word.charAt(0).toUpperCase() + word.substring(1);
      });
      return wordsArr.join(' ');
    },
  },
  watch: {
    isModalOpen() {
      if (this.isModalOpen) {
        this.loading = false;
      }
    },
  },
  components: {
    cbAssistance: Assistance,
    cbButton: Button,
    cbNavigation: Navigation,
  },
};
</script>

<style lang="scss" scoped>
.cb-choose-location {
  &__container {
    position: relative;
  }

  &__header {
    background-color: $azure;
    color: white;
    padding: 48px 40px 56px;

    .cb-heading-4,
    .cb-body-1 {
      max-width: 682px;
      margin: 0 auto;
    }

    .cb-heading-4 {
      margin-bottom: 12px;
    }
  }

  &__body {
    padding: 0 20px;
    margin: -28px auto 40px;
    max-width: 682px;

    &__tile {
      display: flex;
      align-items: center;
      max-width: 295px;
      position: relative;
      cursor: pointer;
      user-select: none;
      background-color: white;
      box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.05),
        0 8px 44px 0 rgba(0, 0, 0, 0.08);
      border-radius: 10px;
      transition: color 0.1s ease, background-color 0.1s ease;
      padding: 20px 47px 20px 20px;
      box-sizing: border-box;
      margin: 0 auto 16px;
      font-weight: 600;

      .cb-overline {
        line-height: 22px;
      }

      &:hover {
        background-color: $lighter-grey;
      }

      &.checked {
        background-color: $azure;
        color: white;
        border: 1px solid white;

        p:first-of-type {
          color: white;
        }

        &::after {
          height: 24px;
          width: 24px;
          content: ' ';
          background-image: url('../assets/icons/checkmark2.svg');
          background-repeat: no-repeat;
          background-size: contain;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: 12px;
          z-index: 1;
        }
      }

      input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
      }
    }
  }

  &__footer {
    padding: 0 20px;
    max-width: 682px;
    margin: 0 auto 28px;
    text-align: center;

    .cb-button {
      width: 100%;
      max-width: 335px;
      margin-bottom: 40px;
    }
  }

  .nav-button {
    position: absolute;
    border: 0;
    padding: 0;
    top: 20px;
    right: 20px;
    background-color: transparent;
    height: 24px;
    width: 24px;

    img {
      height: 100%;
      width: 100%;
    }
  }

  @include tablet {
    &__header {
      text-align: center;
    }
  }

  @include desktop {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    min-height: 100vh;

    &__container {
      width: 682px;
      min-height: 591px;
      background-color: white;
      border-radius: 10px;
    }

    &__header {
      border-radius: 10px 10px 0 0;
      padding: 40px 0 28px;

      .cb-heading-4,
      .cb-body-1 {
        max-width: 366px;
        margin: 0 auto;
      }

      .cb-heading-4 {
        margin-bottom: 12px;
      }

      .cb-body-1 {
        font-size: 18px;
        line-height: 27px;
      }
    }

    &__body {
      margin: 0;
      padding: 26px 0 29px;
      max-height: 310px;
      box-sizing: border-box;
      overflow: scroll;

      label:last-of-type {
        margin-bottom: 0;
      }

      &__tile {
        margin: 0 auto 20px;
      }
    }
    &__footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 28px 0;
      margin: 0 28px;
      border-top: 1px solid $light-grey;

      .cb-button {
        width: fit-content;
        margin-bottom: 28px;
      }
    }
  }
}
</style>
